import {Overlay, OverlayConfig, OverlayRef} from '@angular/cdk/overlay';
import {ComponentPortal} from '@angular/cdk/portal';
import {Injectable, Injector} from '@angular/core';
import {SafeUrl} from '@angular/platform-browser';

import {MEDIA_DATA, MediaData, MediaPreviewComponent, PreviewMediaType} from './media-preview.component';

/**
 * MediaPreviewService makes it easy to create media overlays that show an image or YouTube video
 * at a high resolution.
 * One-pager: go/orb-media-preview-service
 */
@Injectable({providedIn: 'root'})
export class MediaPreviewService {
  constructor(
      private readonly injector: Injector,
      private readonly overlay: Overlay,
  ) {}

  /**
   * previewImage creates an image lightbox that shows the given image at full resolution or 75% of
   * viewport width/height, whichever is smaller.
   * @param src SafeUrl of the image to show.
   * @returns an OverlayRef for the overlay that was created.
   */
  previewImage(src: SafeUrl): OverlayRef {
    const overlayRef = this.overlay.create(this.createOverlayConfig());
    const injector = this.createInjector({type: PreviewMediaType.Image, src: src}, overlayRef);
    const portal = new ComponentPortal(MediaPreviewComponent, null, injector);
    overlayRef.attach(portal);
    return overlayRef;
  }

  /**
   * previewYouTube creates a lightbox that displays a YouTube embed of the video with the given ID.
   * It is displayed at 573x320px or 75% of viewport width/height, whichever is smaller. The
   * dimensions are based on the dimensions of YouTube videos displayed in the v2 Catalog products.
   * @param id Video ID of the video to display.
   * @returns an OverlayRef for the overlay that was created.
   */
  previewYouTube(id: string): OverlayRef {
    const overlayRef = this.overlay.create(this.createOverlayConfig());
    const injector = this.createInjector({type: PreviewMediaType.YouTube, src: id}, overlayRef);
    const portal = new ComponentPortal(MediaPreviewComponent, null, injector);
    overlayRef.attach(portal);
    return overlayRef;
  }

  private createOverlayConfig(): OverlayConfig {
    return new OverlayConfig({
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically()
    });
  }

  private createInjector(data: MediaData, overlayRef: OverlayRef): Injector {
  return Injector.create({
    providers: [
      {
        provide: OverlayRef,
        useValue: overlayRef
      },
      {
        provide: MEDIA_DATA,
        useValue: data
      }
    ],
    parent: this.injector
  });
}
}
